import { IDBPDatabase, IDBPObjectStore, IDBPTransaction, OpenDBCallbacks, openDB, StoreKey, StoreNames, StoreValue } from 'idb';

import { logger, ASSET_TYPES_STORE, ASSETS_STORE, IndexedService, INSPECTIONS_STORE, ISSUE_CFGS_STORE, ISSUE_MEDIAS_STORE, ISSUES_STORE, NOTES_STORE, OFFLINE_WAREHOUSES_STORE, OUTLINES_STORE, SAVED_SEARCH_STORE, SYSTEM2DS_STORE, TEXTS_STORE, UPRIGHTS_COLUMNS_STORE, UPRIGHTS_STORE, WAREHOUSES_STORE } from '@damofire/data-commons';

import { isConnectionError, RETRY_DELAY_MS } from './idb-retry';
import { PersistenceFailureHandler, PersistenceFailure } from './persistence-failure';

/** Upper bound for waitAllDone(): a stuck transaction or connection must not block its callers forever. */
export const WAIT_ALL_DONE_TIMEOUT_MS = 2 * 60 * 1000;

const DIVISION_ID_PATH = 'division._id';
const ORGANIZATION_ID_PATH = 'organization._id';
const ORGANIZATION_ID_PROPERTY = 'organizationId';
const ASSET_INDEX = 'asset';
const SYSTEM_INDEX = 'system';
const ID_INDEX = 'id';
const ISSUE_ID_INDEX = 'issueId';
const ORGANIZATION_INDEX = 'organization';
const SECTION_INDEX = 'section';

const KEY_OPTIONS = { keyPath: [DIVISION_ID_PATH, '_id'] };
const KEY_OPS_OPTIONS = { keyPath: [DIVISION_ID_PATH, '_id', 'operation'] };

const createIfNotExist = (
  transaction: IDBPTransaction<unknown, string[], 'versionchange'>,
  storeName: string,
  optionalParameters?: IDBObjectStoreParameters,
): IDBPObjectStore<unknown, StoreNames<unknown>[], string, 'versionchange'> => {
  logger.debug('createIfNotExist', { storeName, category: 'idb' });
  if (!(transaction.objectStoreNames as unknown as DOMStringList).contains(storeName)) {
    logger.debug('Creating store', { storeName, category: 'idb' });
    return transaction.db.createObjectStore(storeName, optionalParameters);
  }

  return null;
};

const applyBaseStoreIndexes = (store: IDBPObjectStore<unknown, string[], string, 'versionchange'>) => {
  store?.createIndex('division', DIVISION_ID_PATH, { unique: false });
  store?.createIndex(ID_INDEX, '_id', { unique: true });
};

const applyOperationsStoreIndexes = (store: IDBPObjectStore<unknown, string[], string, 'versionchange'>) => {
  applyBaseStoreIndexes(store);
  store?.createIndex('operation', 'operation', { unique: false });
};

const IDB_DAMOFIRE_VERSION = 15;
const IDB_DAMOFIRE_NAME = 'damofire';

export type IdbTransaction = IDBPTransaction<unknown, [string], IDBTransactionMode>;

export interface IdbOpContext {
  storeName: string;
  transaction: IdbTransaction | undefined;
  operation: string;
}

const upgradeDamofireDatabase: NonNullable<OpenDBCallbacks<unknown>['upgrade']> = async (db, oldVersion, newVersion, transaction) => {
  logger.info('Upgrading IndexedDB', { oldVersion, newVersion, category: 'idb' });
  const assets = createIfNotExist(transaction, ASSETS_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(assets);

  const assetsOperations = createIfNotExist(transaction, `${ASSETS_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(assetsOperations);

  const inspections = createIfNotExist(transaction, INSPECTIONS_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(inspections);

  const inspectionsOperations = createIfNotExist(transaction, `${INSPECTIONS_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(inspectionsOperations);

  const issues = createIfNotExist(transaction, ISSUES_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(issues);
  issues?.createIndex(ASSET_INDEX, 'asset._id', { unique: false });
  issues?.createIndex(SECTION_INDEX, 'section', { unique: false });

  const issuesOperations = createIfNotExist(transaction, `${ISSUES_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(issuesOperations);

  const issueMedia = createIfNotExist(transaction, ISSUE_MEDIAS_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(issueMedia);
  issueMedia?.createIndex(ISSUE_ID_INDEX, 'issue._id', { unique: false });

  const issueMediaOperations = createIfNotExist(transaction, `${ISSUE_MEDIAS_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(issueMediaOperations);

  const columns = createIfNotExist(transaction, UPRIGHTS_COLUMNS_STORE, KEY_OPTIONS);
  columns?.createIndex('division', DIVISION_ID_PATH, { unique: false });
  columns?.createIndex(ASSET_INDEX, 'assetId', { unique: false });

  const outlines = createIfNotExist(transaction, OUTLINES_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(outlines);

  const outlinesOperations = createIfNotExist(transaction, `${OUTLINES_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(outlinesOperations);

  const system2ds = createIfNotExist(transaction, SYSTEM2DS_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(system2ds);
  system2ds?.createIndex('by-section-division', ['section', DIVISION_ID_PATH], { unique: false });

  if (oldVersion < 14 && newVersion >= 14 && !system2ds) {
    const system2dsExisting = transaction.objectStore(SYSTEM2DS_STORE);
    if (system2dsExisting && !Object.values(system2dsExisting.indexNames).includes('by-section-division')) {
      system2dsExisting.createIndex('by-section-division', ['section', DIVISION_ID_PATH], { unique: false });
    }
  }

  const system2dsOperations = createIfNotExist(transaction, `${SYSTEM2DS_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(system2dsOperations);

  const warehouses = createIfNotExist(transaction, WAREHOUSES_STORE, { keyPath: '_id' });
  warehouses?.createIndex(ID_INDEX, '_id', { unique: true });
  warehouses?.createIndex(ORGANIZATION_INDEX, ORGANIZATION_ID_PATH, { unique: false });

  const offlineWarehouses = createIfNotExist(transaction, OFFLINE_WAREHOUSES_STORE, { keyPath: '_id' });
  offlineWarehouses?.createIndex(ID_INDEX, '_id', { unique: true });

  if (oldVersion <= 10 && (transaction.objectStoreNames as unknown as DOMStringList).contains(ASSET_TYPES_STORE)) {
    transaction.db.deleteObjectStore(ASSET_TYPES_STORE);
  }
  const assetTypes = createIfNotExist(transaction, ASSET_TYPES_STORE, { keyPath: 'description' });
  assetTypes?.createIndex('description', 'description', { unique: false });

  const issueConfigs = createIfNotExist(transaction, ISSUE_CFGS_STORE, { keyPath: ORGANIZATION_ID_PROPERTY });
  issueConfigs?.createIndex(ORGANIZATION_INDEX, ORGANIZATION_ID_PROPERTY, { unique: true });

  const uprights = createIfNotExist(transaction, UPRIGHTS_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(uprights);
  uprights?.createIndex(SYSTEM_INDEX, 'systemId', { unique: false });

  const notes = createIfNotExist(transaction, NOTES_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(notes);

  const notesOperations = createIfNotExist(transaction, `${NOTES_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(notesOperations);

  const savedSearches = createIfNotExist(transaction, SAVED_SEARCH_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(savedSearches);

  const savedSearchesOperations = createIfNotExist(transaction, `${SAVED_SEARCH_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(savedSearchesOperations);

  const layoutTexts = createIfNotExist(transaction, TEXTS_STORE, KEY_OPTIONS);
  applyBaseStoreIndexes(layoutTexts);

  const layoutTextsOperations = createIfNotExist(transaction, `${TEXTS_STORE}-operations`, KEY_OPS_OPTIONS);
  applyOperationsStoreIndexes(layoutTextsOperations);

  // add section index on ISSUES when store already exists
  if (oldVersion < 13 && newVersion >= 13 && !issues) {
    const issuesExisting = transaction.objectStore(ISSUES_STORE);
    if (issuesExisting && !Object.values(issuesExisting.indexNames).includes(SECTION_INDEX)) {
      issuesExisting.createIndex(SECTION_INDEX, 'section', { unique: false });
    }
  }
};

export class IdbService extends IndexedService<IdbTransaction> {
  private db: Promise<IDBPDatabase>;
  private _sharedDb: IDBPDatabase;
  private readonly txPool: IdbTransaction[] = [];
  private pendingAcquisitions = 0;
  private shouldAllBeDone = false;
  private failureHandler: PersistenceFailureHandler | undefined;
  private connectionOpen = false;

  constructor(initDb = false) {
    super();

    if (initDb) {
      this.db = new Promise((resolve, reject) => {
        openDB(IDB_DAMOFIRE_NAME, IDB_DAMOFIRE_VERSION, { upgrade: upgradeDamofireDatabase })
          .then((db) => resolve(db))
          .catch(reject);
      });
    } else {
      this.db = openDB(IDB_DAMOFIRE_NAME, IDB_DAMOFIRE_VERSION);
    }

    this.observeOpening();
  }

  private observeOpening(): void {
    this.db.then(
      (inner) => {
        this.wireConnection(inner);
      },
      (error) => {
        logger.error('IndexedDB open failed.', { databaseName: IDB_DAMOFIRE_NAME, databaseVersion: IDB_DAMOFIRE_VERSION, error, category: 'idb' });
      },
    );
  }

  private currentOrigin(): string {
    if (typeof location === 'undefined') {
      return 'unknown';
    }
    return location.origin;
  }

  private wireConnection(inner: IDBPDatabase): IDBPDatabase {
    this.connectionOpen = true;
    try {
      logger.info('IndexedDB opened.', {
        origin: this.currentOrigin(),
        databaseName: IDB_DAMOFIRE_NAME,
        databaseVersion: IDB_DAMOFIRE_VERSION,
        objectStores: Array.from(inner.objectStoreNames ?? []),
        category: 'idb',
      });
    } catch {
      // Logging must never break startup.
    }
    inner.onclose = () => {
      logger.error('IDB closed unexpectedly.', { category: 'idb' });
      if (this._sharedDb === inner) {
        this._sharedDb = undefined;
      }
      this.connectionOpen = false;
      if (globalThis.window !== undefined && globalThis.window) {
        globalThis.window.location.reload();
      } else {
        // No reload outside the browser main thread (e.g. sync workers):
        // install a fresh open promise so the next operation reopens.
        this.db = this.openFreshDatabase();
      }
    };
    return inner;
  }

  private openFreshDatabase(): Promise<IDBPDatabase> {
    return openDB(IDB_DAMOFIRE_NAME, IDB_DAMOFIRE_VERSION, { upgrade: upgradeDamofireDatabase }).then(
      (inner) => this.wireConnection(inner),
      (error: unknown) => {
        logger.error('IndexedDB open failed.', { databaseName: IDB_DAMOFIRE_NAME, databaseVersion: IDB_DAMOFIRE_VERSION, error, category: 'idb' });
        throw error;
      },
    );
  }

  async storeExists(storeName: string): Promise<boolean> {
    return (await this.db).objectStoreNames.contains(storeName);
  }

  async createTransaction(storeName: string): Promise<IdbTransaction> {
    return this.resolveTransaction(storeName, null, 'readwrite');
  }

  /**
   * Commit a caller-owned transaction (e.g. sync workers doing manual
   * objectStore.put in a loop). Awaits both pending requests and tx.done
   * so an abort is never mistaken for success.
   */
  async commitTransaction(tx: IdbTransaction, pendingRequests: Promise<unknown>[] = [], context?: Record<string, unknown>): Promise<void> {
    try {
      await Promise.all([...pendingRequests, tx.done]);
    } catch (e) {
      logger.error('IndexedDB external transaction failed.', { ...context, error: e, category: 'idb' });
      throw e;
    }
  }

  async exists(storeName: string, key: StoreKey<string, string>, transaction?: IdbTransaction): Promise<boolean> {
    const matchingKey = await this.runRead({ storeName, transaction, operation: 'exists' }, (store) => store.getKey(key));
    return !!matchingKey;
  }

  async getByKey<T>(storeName: string, key: StoreKey<string, string>, transaction?: IdbTransaction): Promise<T> {
    return this.runRead({ storeName, transaction, operation: 'getByKey' }, (store) => store.get(key) as Promise<T>);
  }

  async getAllFromIndex<T>(storeName: string, indexName: string, key: StoreKey<string, string>, transaction?: IdbTransaction): Promise<T[]> {
    return this.runRead({ storeName, transaction, operation: 'getAllFromIndex' }, (store) => store.index(indexName).getAll(key) as Promise<T[]>);
  }

  async getAll<T>(storeName: string, transaction?: IdbTransaction): Promise<T[]> {
    return this.runRead({ storeName, transaction, operation: 'getAll' }, (store) => store.getAll() as Promise<T[]>);
  }

  async deleteAllFromIndex(stores: string[], indexName: string, key: StoreKey<string, string>, transaction?: IdbTransaction): Promise<void> {
    // Two phases: collect keys first (readonly), then delete with fresh
    // write transactions. Reusing the read tx for deletes races with
    // auto-commit once the getAllKeys request settles.
    for (const storeName of stores) {
      const exists = await this.storeExists(storeName);
      if (!exists) {
        logger.warn('Store not found in IDB, skipping deleteAllFromIndex.', { storeName, category: 'idb' });
        continue;
      }
      const keys = await this.runRead({ storeName, transaction, operation: 'deleteAllFromIndex/readKeys' }, (store) => store.index(indexName).getAllKeys(key));
      // Fresh write tx per store; never reuse the (possibly finished) read tx.
      // If caller passed an explicit tx covering this store, honor it.
      await this.deleteAll(storeName, keys, transaction);
    }
  }

  async add(storeName: string, entry: unknown, transaction?: IdbTransaction): Promise<void> {
    await this.runWrite({ storeName, transaction, operation: 'add' }, (store) => [store.add(entry)]);
  }

  async delete(storeName: string, key: StoreKey<string, string>, transaction?: IdbTransaction): Promise<void> {
    await this.runWrite({ storeName, transaction, operation: 'delete' }, (store) => [store.delete(key)]);
  }

  async deleteAll(storeName: string, keys: StoreValue<unknown, string>[], transaction?: IdbTransaction): Promise<void> {
    if (!keys?.length) {
      return;
    }
    await this.runWrite({ storeName, transaction, operation: 'deleteAll' }, (store) => keys.map((k) => store.delete(k)));
  }

  async addAll(storeName: string, entries: StoreValue<unknown, string>[], transaction?: IdbTransaction): Promise<void> {
    if (!entries?.length) {
      return;
    }
    await this.runWrite({ storeName, transaction, operation: 'addAll' }, (store) => entries.map((k) => store.add(k)));
  }

  async updateAll(storeName: string, entries: StoreValue<unknown, string>[], transaction?: IdbTransaction): Promise<void> {
    if (!entries?.length) {
      return;
    }
    await this.runWrite({ storeName, transaction, operation: 'updateAll' }, (store) => entries.map((k) => store.put(k)));
  }

  async update(storeName: string, entry: unknown, transaction?: IdbTransaction): Promise<void> {
    await this.runWrite({ storeName, transaction, operation: 'update' }, (store) => [store.put(entry)]);
  }

  onPersistenceFailure(handler: PersistenceFailureHandler): void {
    this.failureHandler = handler;
  }

  async addAndVerify(storeName: string, key: StoreKey<string, string>, entry: unknown): Promise<void> {
    await this.add(storeName, entry);
    await this.verifyByKey(storeName, key);
  }

  async updateAndVerify(storeName: string, key: StoreKey<string, string>, entry: unknown): Promise<void> {
    await this.update(storeName, entry);
    await this.verifyByKey(storeName, key);
  }

  private async verifyByKey(storeName: string, key: StoreKey<string, string>): Promise<void> {
    const found = await this.getByKey<unknown>(storeName, key);
    if (found === undefined) {
      throw new Error(`IndexedDB verification failed for key in store ${storeName}`);
    }
  }

  async done(): Promise<void> {
    this.shouldAllBeDone = true;
    (await this.db).close();
    this.connectionOpen = false;
  }

  /**
   * Snapshot of IndexedDB identity and connection state for diagnostics.
   * Origin allow-listing, store-name/version drift checks, and
   * onupgradeneeded audits are caller-side concerns — the lib cannot know
   * valid origins per environment (capacitor://localhost vs http://localhost);
   * it exposes the snapshot, callers assert.
   */
  async diagnose(): Promise<{
    origin: string;
    databaseName: string;
    databaseVersion: number;
    objectStores: string[];
    connectionOpen: boolean;
    pendingTransactions: number;
  }> {
    const db = await this.db;
    return {
      origin: this.currentOrigin(),
      databaseName: db.name,
      databaseVersion: db.version,
      objectStores: Array.from(db.objectStoreNames ?? []),
      connectionOpen: this.connectionOpen,
      pendingTransactions: this.txPool.length,
    };
  }

  async waitAllDone(timeoutMs: number = WAIT_ALL_DONE_TIMEOUT_MS): Promise<void> {
    logger.debug('waitAllDone IndexedDB', { category: 'idb' });
    const deadline = Date.now() + timeoutMs;
    // Drain loop, not one snapshot: transactions started while waiting join
    // the next round, as do operations still resolving their transaction
    // (tracked via pendingAcquisitions). The emptiness check runs with no
    // await in between, so no transaction can slip in between the check and
    // the return. The macrotask yield avoids starving the event loop while
    // an acquisition waits on the database connection. The deadline keeps a
    // stuck transaction or connection from blocking the caller forever.
    for (;;) {
      const pending = [...this.txPool];
      if (pending.length === 0 && this.pendingAcquisitions === 0) {
        return;
      }
      const remainingMs = deadline - Date.now();
      if (remainingMs <= 0) {
        this.reportDrainTimeout(timeoutMs, pending.length);
        return;
      }
      if (pending.length === 0) {
        await this.delay(0);
        continue;
      }
      const allToWait: Promise<void>[] = [...pending.map((tx: IdbTransaction): Promise<void> =>
        tx.done.catch((e: unknown): void => {
          logger.debug('IndexedDB pooled transaction already reported as failed.', { error: e, category: 'idb' });
        }),
      )];
      await this.waitAtMost(Promise.all(allToWait), remainingMs);
    }
  }

  private reportDrainTimeout(timeoutMs: number, pendingTransactions: number): void {
    logger.error('waitAllDone gave up waiting for IndexedDB, continuing with work still pending.', {
      timeoutMs,
      pendingTransactions,
      pendingAcquisitions: this.pendingAcquisitions,
      category: 'idb',
    });
  }

  /** Resolves when `work` settles or `ms` elapses, whichever comes first. Never rejects, and never leaves its timer running. */
  private async waitAtMost(work: Promise<unknown>, ms: number): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, ms);
    });
    try {
      await Promise.race([work.catch((): void => undefined), timeout]);
    } finally {
      clearTimeout(timer);
    }
  }

  private async resolveTransaction(storeName: string, transaction?: IdbTransaction, mode: IDBTransactionMode = 'readonly'): Promise<IdbTransaction> {
    // Counted synchronously so waitAllDone() sees operations that have
    // started but are still awaiting the database connection below.
    this.pendingAcquisitions++;
    try {
      if (this.shouldAllBeDone) {
        throw new Error('IdbService - shouldAllBeDone was set');
      }

      if (transaction) {
        return transaction;
      }

      if (this._sharedDb === undefined) {
        this._sharedDb = await this.db;
      }

      try {
        const tx = this._sharedDb.transaction(storeName, mode);
        this.trackTransaction(tx);
        return tx;
      } catch (e) {
        return this.createWithReopen(storeName, mode, e);
      }
    } finally {
      this.pendingAcquisitions--;
    }
  }

  private async createWithReopen(storeName: string, mode: IDBTransactionMode, error?: unknown): Promise<IdbTransaction> {
    // Transaction creation threw synchronously (e.g. connection closed /
    // InvalidStateError after iOS background). Reopen once and retry.
    logger.error('IndexedDB transaction creation failed, reopening connection.', { storeName, mode, error, category: 'idb' });
    await this.resetConnection();
    this._sharedDb = await this.db;
    const tx = this._sharedDb.transaction(storeName, mode);
    this.trackTransaction(tx);
    return tx;
  }

  private trackTransaction(tx: IdbTransaction): void {
    this.txPool.push(tx);
    const remove = () => {
      const index = this.txPool.indexOf(tx);
      if (index >= 0) {
        this.txPool.splice(index, 1);
      }
    };
    // Avoid unhandled rejection from the derived promise when tx aborts.
    tx.done.then(remove, remove);
  }

  private async runRead<T>(
    ctx: IdbOpContext,
    fn: (store: ReturnType<IdbTransaction['objectStore']>) => Promise<T>,
  ): Promise<T> {
    const ownsTx = !ctx.transaction;
    try {
      const tx = await this.resolveTransaction(ctx.storeName, ctx.transaction, 'readonly');
      const store = tx.objectStore(ctx.storeName);
      // Await inside the same tick: returning the bare promise breaks the
      // try/catch fallback and can outlive the transaction.
      return await fn(store);
    } catch (e) {
      logger.error('IndexedDB read failed.', { storeName: ctx.storeName, operation: ctx.operation, error: e, category: 'idb' });
      if (ownsTx && isConnectionError(e)) {
        return this.retryWithFreshConnection(ctx, 'readonly', async (tx: IdbTransaction): Promise<T> => {
          try {
            return await fn(tx.objectStore(ctx.storeName));
          } catch (retryError) {
            logger.error('IndexedDB read retry failed.', { storeName: ctx.storeName, operation: ctx.operation, error: retryError, category: 'idb' });
            throw retryError;
          }
        });
      }
      throw e;
    }
  }

  private async runWrite(
    ctx: IdbOpContext,
    fn: (store: ReturnType<IdbTransaction['objectStore']>) => Promise<unknown>[] | Promise<unknown>,
  ): Promise<void> {
    const ownsTx = !ctx.transaction;
    const attempt = async (): Promise<void> => {
      const tx = await this.resolveTransaction(ctx.storeName, ctx.transaction, 'readwrite');
      let requests: Promise<unknown>[];
      try {
        const produced = fn(tx.objectStore(ctx.storeName));
        requests = Array.isArray(produced) ? produced : [produced];
      } catch (syncError) {
        // Synchronous throw (e.g. TransactionInactiveError on request creation).
        logger.error('IndexedDB write request creation failed.', { storeName: ctx.storeName, operation: ctx.operation, error: syncError, category: 'idb' });
        throw syncError;
      }
      await this.commitRequests(tx, requests, ctx, ownsTx);
    };

    try {
      await attempt();
    } catch (e) {
      if (ownsTx && isConnectionError(e)) {
        try {
          // Retry once with a fresh connection/transaction, then give up.
          await this.retryWithFreshConnection(ctx, 'readwrite', async (tx: IdbTransaction): Promise<void> => {
            const produced = fn(tx.objectStore(ctx.storeName));
            const requests = Array.isArray(produced) ? produced : [produced];
            await this.commitRequests(tx, requests, ctx, true);
            logger.info('IndexedDB write retry succeeded.', { storeName: ctx.storeName, operation: ctx.operation, category: 'idb' });
          });
          return;
        } catch (retryError) {
          logger.error('IndexedDB write retry failed, data NOT persisted.', { storeName: ctx.storeName, operation: ctx.operation, error: retryError, category: 'idb' });
          this.notifyPersistenceFailure(ctx, retryError, 2);
          throw retryError;
        }
      }
      // Non-retryable terminal failure: single attempt, no retry happened.
      this.notifyPersistenceFailure(ctx, e, 1);
      throw e;
    }
  }

  private notifyPersistenceFailure(ctx: IdbOpContext, error: unknown, attempts: number): void {
    const failure: PersistenceFailure = { storeName: ctx.storeName, operation: ctx.operation, error, attempts, happenedAt: new Date().toISOString() };
    logger.error('IndexedDB persistence failed terminally, data NOT persisted.', { ...failure, category: 'idb' });
    void Promise.resolve()
      .then(() => this.failureHandler?.(failure))
      .catch((handlerError: unknown) => {
        logger.error('IndexedDB failure handler threw, failure kept in logs only.', { error: handlerError, category: 'idb' });
      });
  }

  private async commitRequests(tx: IdbTransaction, requests: Promise<unknown>[], ctx: IdbOpContext, awaitCommit: boolean): Promise<void> {
    try {
      // Critical for owned transactions: await BOTH individual requests AND
      // tx.done (oncomplete). Request success alone does not guarantee the
      // transaction commits; tx.done rejects on abort/error
      // (UnknownError, AbortError, QuotaExceeded...).
      // Caller-owned transactions are excluded: awaiting their completion
      // here would finish the tx before the caller enqueues its next write.
      await (awaitCommit ? Promise.all([...requests, tx.done]) : Promise.all(requests));
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') {
        logger.error('IndexedDB transaction aborted, data NOT persisted.', { storeName: ctx.storeName, operation: ctx.operation, error: e, category: 'idb' });
      } else {
        logger.error('IndexedDB write request failed, data NOT persisted.', { storeName: ctx.storeName, operation: ctx.operation, error: e, category: 'idb' });
      }
      throw e;
    }
  }

  private async retryWithFreshConnection<T>(ctx: IdbOpContext, mode: IDBTransactionMode, onFreshTx: (tx: IdbTransaction) => Promise<T>): Promise<T> {
    await this.resetConnection();
    await this.delay(RETRY_DELAY_MS);
    const tx = await this.resolveTransaction(ctx.storeName, undefined, mode);
    return onFreshTx(tx);
  }

  private async resetConnection(): Promise<void> {
    try {
      this._sharedDb?.close();
    } catch (e) {
      logger.debug('IndexedDB connection close during reset failed, continuing.', { error: e, category: 'idb' });
    }
    this._sharedDb = undefined;
    this.connectionOpen = false;
    // Install a fresh open promise immediately: the previous promise resolves
    // to the connection just closed, and every consumer re-awaits this.db.
    this.db = this.openFreshDatabase();
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async exportJson(): Promise<Record<string, string>> {
    const exportedData: Record<string, string> = {};
    for (const storeName of (await this.db).objectStoreNames) {
      exportedData[storeName] = JSON.stringify(await this.getAll(storeName));
    }

    return exportedData;
  }

  async clear(): Promise<void> {
    const db = await this.db;
    for (const storeName of db.objectStoreNames) {
      const tx = db.transaction(storeName, 'readwrite');
      this.trackTransaction(tx as unknown as IdbTransaction);
      const store = tx.objectStore(storeName);
      try {
        await Promise.all([store.clear(), tx.done]);
      } catch (e) {
        logger.error('IndexedDB clear failed.', { storeName, error: e, category: 'idb' });
        throw e;
      }
    }

    // Do NOT close the DB here: waitAllDone used to call done(), which sets
    // shouldAllBeDone and permanently disables the service.
    await this.waitAllDone();
  }
}
